import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { fetchTenants } from '../../api/admin';
import TenantDetailDrawer from '../../components/admin/TenantDetailDrawer';
import StatusBadge from '../../components/admin/StatusBadge';
import DashboardShell from '../../components/ui/DashboardShell';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import SearchAutocomplete from '../../components/ui/SearchAutocomplete';
import { useDebounce } from '../../hooks/useDebounce';

const STATUS_TABS = ['', 'PENDING', 'ACTIVE', 'SUSPENDED', 'CANCELLED'];

const AdminTenantsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || '';
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');
  const debouncedInput = useDebounce(searchInput, 300);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedId, setSelectedId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-tenants', status, appliedSearch, page, pageSize],
    queryFn: () =>
      fetchTenants({
        status: status || undefined,
        search: appliedSearch || undefined,
        page,
        limit: pageSize,
      }),
  });

  const { data: suggestionData, isFetching: suggestionsLoading } = useQuery({
    queryKey: ['admin-tenant-suggestions', debouncedInput, status],
    queryFn: () =>
      fetchTenants({
        search: debouncedInput,
        status: status || undefined,
        page: 1,
        limit: 8,
      }),
    enabled: debouncedInput.trim().length >= 1,
  });

  const suggestions = (suggestionData?.data || []).map((tenant) => ({
    label: tenant.businessName,
    value: tenant.businessName,
    hint: tenant.address,
  }));

  const commitSearch = (value) => {
    const v =
      value && typeof value === 'object'
        ? String(value.value ?? value.label ?? '').trim()
        : String(value ?? searchInput ?? '').trim();
    setSearchInput(v);
    setAppliedSearch(v);
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (v) next.set('search', v);
    else next.delete('search');
    setSearchParams(next);
  };

  const statusLabel = (tab) => (tab ? t(`status.tenant.${tab}`) : t('common.all'));

  return (
    <DashboardShell title={t('admin.tenants.title')} subtitle={t('admin.tenants.manageAllSubtitle')}>
      <div className="mb-6 flex flex-wrap gap-2">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab || 'ALL'}
            type="button"
            onClick={() => {
              setPage(1);
              const next = new URLSearchParams(searchParams);
              if (tab) next.set('status', tab);
              else next.delete('status');
              setSearchParams(next);
            }}
            className={`rounded-lg px-3 py-1.5 text-sm ${
              status === tab
                ? 'bg-glow-600/20 text-glow-300'
                : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800'
            }`}
          >
            {statusLabel(tab)}
          </button>
        ))}
      </div>

      <SearchAutocomplete
        className="mb-6 max-w-xl"
        value={searchInput}
        onChange={setSearchInput}
        onSearch={commitSearch}
        suggestions={suggestions}
        loading={suggestionsLoading}
        placeholder={t('admin.tenants.searchPlaceholder')}
      />

      <div className="overflow-x-auto rounded-xl border border-zinc-200 dark:border-zinc-800">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 text-zinc-600 dark:text-zinc-500">
            <tr>
              <th className="px-4 py-3">{t('admin.tenants.salon')}</th>
              <th className="px-4 py-3">{t('admin.tenants.owner')}</th>
              <th className="px-4 py-3">{t('common.email')}</th>
              <th className="px-4 py-3">{t('admin.tenants.status')}</th>
              <th className="px-4 py-3">{t('admin.tenants.plan')}</th>
              <th className="px-4 py-3">{t('admin.tenants.requestedAt')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-zinc-600 dark:text-zinc-500">
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {(data?.data || []).map((tenant) => (
              <tr
                key={tenant._id}
                onClick={() => setSelectedId(tenant._id)}
                className="cursor-pointer border-b border-zinc-200/50 dark:border-zinc-800/50 hover:bg-zinc-200/30 dark:bg-zinc-800/30"
              >
                <td className="px-4 py-3 font-medium">{tenant.businessName}</td>
                <td className="px-4 py-3">
                  {tenant.ownerUser
                    ? `${tenant.ownerUser.firstName} ${tenant.ownerUser.lastName}`
                    : tenant.ownerName}
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-400">{tenant.email}</td>
                <td className="px-4 py-3">
                  <StatusBadge status={tenant.subscriptionStatus} />
                </td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">{tenant.subscriptionPlan}</td>
                <td className="px-4 py-3 text-zinc-600 dark:text-zinc-500">
                  {new Date(tenant.requestedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PaginatedFooter
        pagination={data?.pagination}
        page={page}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />

      <TenantDetailDrawer tenantId={selectedId} onClose={() => setSelectedId(null)} />
    </DashboardShell>
  );
};

export default AdminTenantsPage;
